import { ChangeDetectionStrategy, Component, computed, effect, inject, output, signal } from '@angular/core';
import { ButtonComponent, ButtonType } from '../../shared/components/button/button.component';
import { LayoutStore, LayoutStoreType } from '../../core/store/layout.store';
import { NgOptimizedImage } from '@angular/common';
import { Theme, ThemeService } from '../../core/services/theme.service';

@Component({
    selector: 'juyo-tour-guide',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, NgOptimizedImage],
    styleUrl: 'tour-guide.scss',
    template: `
        @if (stepIndex() === 0) {
            <div class="overlay">
                <div class="welcome-card">
                    <div class="icon-area">
                        <img
                            [ngSrc]="
                                themeService.theme() === Theme.Dark
                                    ? 'assets/logos/logo_icon_white.png'
                                    : 'assets/logos/logo_icon_dark.png'
                            "
                            width="40"
                            height="40"
                            alt="Juyo"
                            priority
                        />
                    </div>
                    <div class="card-body">
                        <div class="progress-dots">
                            @for (step of steps; track $index) {
                                <span class="dot" [class.active]="$index === stepIndex()"></span>
                            }
                        </div>
                        <h2 class="title">{{ current().title }}</h2>
                        <p class="description">{{ current().description }}</p>
                        <div class="welcome-actions">
                            <button class="skip-btn" type="button" (click)="onClose()">Skip tour</button>
                            <juyo-button [buttonType]="ButtonType.PURPLE" (clicked)="onNext()">
                                {{ current().primaryButtonText }}
                                <span class="material-symbols-outlined">arrow_right_alt</span>
                            </juyo-button>
                        </div>
                    </div>
                </div>
            </div>
        } @else {
            <div class="overlay light" (click)="onClose()"></div>
            <div
                class="tooltip-card"
                [style.top.px]="current().cardTop ?? null"
                [style.bottom.px]="current().cardBottom ?? null"
            >
                <div class="tooltip-header">
                    <span class="step-label">{{ current().label }}</span>
                    <div class="progress-dots">
                        @for (step of steps; track $index) {
                            <span class="dot" [class.active]="$index === stepIndex()"></span>
                        }
                    </div>
                </div>
                <h3 class="title">{{ current().title }}</h3>
                <p class="description">{{ current().description }}</p>
                <div class="tooltip-actions">
                    <button class="skip-btn" type="button" (click)="onClose()">Skip</button>
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" [heightInPx]="30" (clicked)="onBack()">
                        <span class="material-symbols-outlined">arrow_left_alt</span> Back
                    </juyo-button>
                    <juyo-button
                        [heightInPx]="30"
                        (clicked)="onNext()"
                    >
                        {{ current().primaryButtonText }} <span class="material-symbols-outlined">arrow_right_alt</span>
                    </juyo-button>
                </div>
            </div>
        }
    `,
})
export class TourGuideComponent {
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    protected themeService = inject(ThemeService);

    public closed = output<void>();

    protected readonly steps = STEPS;
    protected readonly ButtonType = ButtonType;

    protected stepIndex = signal(0);
    protected current = computed(() => STEPS[this.stepIndex()]);
    protected isLastStep = computed(() => this.stepIndex() === STEPS.length - 1);

    constructor() {
        effect(() => {
            this.layoutStore.setTourStep(this.stepIndex());
        });
    }

    protected onNext(): void {
        if (this.isLastStep()) {
            this.onClose();
            return;
        }
        this.stepIndex.update((i) => i + 1);
    }

    protected onBack(): void {
        this.stepIndex.update((i) => i - 1);
    }

    protected onClose(): void {
        this.layoutStore.setTourStep(null);
        this.closed.emit();
    }

    protected readonly Theme = Theme;
}

interface TourStep {
    label: string;
    title: string;
    description: string;
    primaryButtonText: string;
    /** Distance from viewport top to position the tooltip card, in px. */
    cardTop?: number;
    /** Distance from viewport bottom to position the tooltip card, in px. */
    cardBottom?: number;
}

const STEPS: TourStep[] = [
    {
        label: '',
        title: 'Welcome to Juyo',
        description: "You're in! Let us help guide you from data to insights in minutes.",
        primaryButtonText: "Let's get started",
        // step 0 shows the centred welcome card — no cardTop/cardBottom needed
    },
    {
        label: 'STEP 2 OF 4',
        title: 'Connect your Data',
        description:
            'You can add properties, connect your data and manage users under "Settings". You can find it any time inside your account menu.',
        primaryButtonText: 'Next',
        cardBottom: 10, // points at Settings in the user menu (bottom of sidebar)
    },
    {
        label: 'STEP 3 OF 4',
        title: 'Try Data Drop',
        description: 'Upload spreadsheets for instant AI analysis while your PMS connection is being set up.',
        primaryButtonText: 'Next',
        cardTop: 116, // points at Data Drop
    },
    {
        label: 'STEP 4 OF 4',
        title: 'Try HotelGPT',
        description:
            'Ask HotelGPT any revenue management question. It connects to your data sources to give you real answers, not just charts.',
        primaryButtonText: 'Get started',
        cardTop: 68, // points at HotelGPT
    },
];
